import { Filesystem, Directory } from '@capacitor/filesystem';
import { supabase } from '../supabaseClient';
import { auth } from '../firebase';
import { safeStorage } from '../utils/safeStorage';
import Logger from '../utils/Logger';

export interface Artwork {
    id: string;
    category: string;
    date: string;
    fileName: string;
    thumbFileName?: string;
    videoFileName?: string;
    isSynced?: boolean;
}

const STORAGE_KEY_PREFIX = 'ar_drawing_collection';
const GUEST_SCOPE = 'guest';

export const GalleryService = {
    getStorageKey(userId: string | null = auth.currentUser?.uid ?? null): string {
        return `${STORAGE_KEY_PREFIX}:${userId || GUEST_SCOPE}`;
    },

    saveMetadata(metadata: Artwork[]) {
        safeStorage.setJSON(this.getStorageKey(), metadata);
    },

    async saveArtwork(imageBlob: Blob, category: string, videoBlob?: Blob): Promise<void> {
        const timestamp = Date.now();
        const imageFileName = `artwork_${timestamp}.jpg`;
        const thumbFileName = `thumb_${timestamp}.jpg`;

        let videoFileName = undefined;
        if (videoBlob) {
            const extension = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
            videoFileName = `recording_${timestamp}.${extension}`;
        }

        try {
            // 1. Save locally
            const imageBase64 = await this.blobToBase64(imageBlob);
            await Filesystem.writeFile({
                path: imageFileName,
                data: imageBase64,
                directory: Directory.Data,
            });

            // Save a lightweight preview thumbnail for faster gallery rendering.
            try {
                const thumbBlob = await this.createThumbnail(imageBlob, 384, 0.8);
                const thumbBase64 = await this.blobToBase64(thumbBlob);
                await Filesystem.writeFile({
                    path: thumbFileName,
                    data: thumbBase64,
                    directory: Directory.Data,
                });
            } catch (thumbnailError) {
                Logger.warn('Failed to generate thumbnail, falling back to full image previews', thumbnailError);
            }

            if (videoBlob && videoFileName) {
                Logger.debug(`[GalleryService] Saving video: ${videoFileName}, size: ${videoBlob.size}`);
                const videoBase64 = await this.blobToBase64(videoBlob);
                await Filesystem.writeFile({
                    path: videoFileName,
                    data: videoBase64,
                    directory: Directory.Data,
                });
            }

            // 2. Cloud Sync (if logged in)
            let isSynced = false;
            const user = auth.currentUser;
            if (user) {
                try {
                    await this.uploadToCloud(user.uid, imageBlob, imageFileName, videoBlob, videoFileName, category);
                    isSynced = true;
                } catch (cloudError) {
                    Logger.error('Cloud upload failed (will retry later)', cloudError);
                }
            }

            // 3. Update metadata
            const newArtwork: Artwork = {
                id: timestamp.toString(),
                category: category || 'Uncategorized',
                date: new Date().toISOString(),
                fileName: imageFileName,
                thumbFileName,
                videoFileName: videoFileName,
                isSynced
            };

            const existing = await this.getMetadata();
            const updated = [newArtwork, ...existing];
            this.saveMetadata(updated);
        } catch (e) {
            Logger.error('Error saving artwork', e);
            throw e;
        }
    },

    blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
                const base64data = reader.result as string;
                resolve(base64data.split(',')[1]);
            };
            reader.onerror = reject;
        });
    },

    async getMetadata(): Promise<Artwork[]> {
        return safeStorage.getJSON<Artwork[]>(this.getStorageKey()) || [];
    },

    async getArtworkSrc(fileName: string): Promise<string> {
        try {
            const result = await Filesystem.readFile({
                path: fileName,
                directory: Directory.Data,
            });
            return `data:${this.getImageMimeType(fileName)};base64,${result.data}`;
        } catch (e) {
            Logger.error('Error loading artwork image', e);
            return '';
        }
    },

    async getArtworkThumbnailSrc(fileName: string, thumbFileName?: string): Promise<string> {
        if (thumbFileName) {
            const thumbSrc = await this.getArtworkSrc(thumbFileName);
            if (thumbSrc) {
                return thumbSrc;
            }
        }

        return this.getArtworkSrc(fileName);
    },

    async getVideoSrc(fileName: string): Promise<string> {
        try {
            const result = await Filesystem.readFile({
                path: fileName,
                directory: Directory.Data,
            });
            const ext = fileName.split('.').pop()?.toLowerCase();
            const mimeType = ext === 'mp4' ? 'video/mp4' : 'video/webm';
            return `data:${mimeType};base64,${result.data}`;
        } catch (e) {
            Logger.error('Error loading video', e);
            return '';
        }
    },

    async deleteArtwork(id: string, fileName: string): Promise<void> {
        try {
            // Delete file
            await Filesystem.deleteFile({
                path: fileName,
                directory: Directory.Data,
            });

            // Find and delete video if exists
            const existing = await this.getMetadata();
            const artwork = existing.find(a => a.id === id);
            if (artwork?.videoFileName) {
                try {
                    await Filesystem.deleteFile({
                        path: artwork.videoFileName,
                        directory: Directory.Data,
                    });
                } catch (e) {
                    Logger.warn('Could not delete associated video', e);
                }
            }

            if (artwork?.thumbFileName) {
                try {
                    await Filesystem.deleteFile({
                        path: artwork.thumbFileName,
                        directory: Directory.Data,
                    });
                } catch (e) {
                    Logger.warn('Could not delete associated thumbnail', e);
                }
            }

            // Update metadata
            const updated = existing.filter(a => a.id !== id);
            this.saveMetadata(updated);

            // Cloud Delete (Best effort)
            const user = auth.currentUser;
            if (user && artwork) {
                const imagePath = `${user.uid}/${fileName}`;
                await supabase.storage.from('user_gallery').remove([imagePath]);
                if (artwork.videoFileName) {
                    await supabase.storage.from('user_gallery').remove([`${user.uid}/${artwork.videoFileName}`]);
                }
                await supabase.from('user_artworks').delete().eq('storage_path_image', imagePath);
            }

        } catch (e) {
            Logger.error('Error deleting artwork', e);
            throw e;
        }
    },

    // --- CLOUD HELPERS ---

    async uploadToCloud(userId: string, imageBlob: Blob, imageFileName: string, videoBlob: Blob | undefined, videoFileName: string | undefined, category: string) {
        // 1. Upload Image
        const imagePath = `${userId}/${imageFileName}`;
        const { error: imgError } = await supabase.storage
            .from('user_gallery')
            .upload(imagePath, imageBlob, { upsert: true });

        if (imgError) throw imgError;

        // 2. Upload Video (if any)
        let videoPath = null;
        if (videoBlob && videoFileName) {
            videoPath = `${userId}/${videoFileName}`;
            const { error: vidError } = await supabase.storage
                .from('user_gallery')
                .upload(videoPath, videoBlob, { upsert: true });

            if (vidError) throw vidError;
        }

        // 3. Insert Record
        const { error: dbError } = await supabase.from('user_artworks').insert({
            user_id: userId,
            category: category,
            storage_path_image: imagePath,
            storage_path_video: videoPath,
            metadata: { fileName: imageFileName, videoFileName: videoFileName }
        });

        if (dbError) throw dbError;
    },

    async syncFromCloud(): Promise<void> {
        const user = auth.currentUser;
        if (!user) return;

        try {
            Logger.debug('Starting Cloud Sync...');
            // 1. Get all cloud artworks
            const { data: cloudArtworks, error } = await supabase
                .from('user_artworks')
                .select('*')
                .eq('user_id', user.uid);

            if (error || !cloudArtworks) return;

            // 2. Get local artworks
            const localArtworks = await this.getMetadata();
            const localFileNames = new Set(localArtworks.map(a => a.fileName));
            let hasChanges = false;

            // 3. Check for missing files
            for (const item of cloudArtworks) {
                const fileName = item.metadata?.fileName || item.storage_path_image.split('/').pop();
                if (!fileName) continue;

                if (!localFileNames.has(fileName)) {
                    Logger.debug('Downloading missing artwork:', fileName);

                    // Download Image
                    const { data: imgData } = await supabase.storage
                        .from('user_gallery')
                        .download(item.storage_path_image);

                    if (imgData) {
                        const imageBase64 = await this.blobToBase64(imgData);
                        await Filesystem.writeFile({
                            path: fileName,
                            data: imageBase64,
                            directory: Directory.Data,
                        });

                        let thumbFileName: string | undefined;
                        try {
                            const thumbBlob = await this.createThumbnail(imgData, 384, 0.8);
                            thumbFileName = `thumb_${item.id}.jpg`;
                            const thumbBase64 = await this.blobToBase64(thumbBlob);
                            await Filesystem.writeFile({
                                path: thumbFileName,
                                data: thumbBase64,
                                directory: Directory.Data,
                            });
                        } catch (thumbnailError) {
                            Logger.warn('Could not generate thumbnail for cloud artwork', thumbnailError);
                        }

                        // Download Video if needed
                        let videoFileName = item.metadata?.videoFileName;
                        if (item.storage_path_video) {
                            videoFileName = videoFileName || item.storage_path_video.split('/').pop();
                            const { data: vidData } = await supabase.storage
                                .from('user_gallery')
                                .download(item.storage_path_video);

                            if (vidData && videoFileName) {
                                const videoBase64 = await this.blobToBase64(vidData);
                                await Filesystem.writeFile({
                                    path: videoFileName,
                                    data: videoBase64,
                                    directory: Directory.Data,
                                });
                            }
                        }

                        // Add to local metadata
                        localArtworks.push({
                            id: item.id, // Use cloud ID or generate new? Cloud ID is UUID, local is timestamp. Let's use timestamp from created_at or just Date.now()
                            category: item.category,
                            date: item.created_at,
                            fileName: fileName,
                            thumbFileName,
                            videoFileName: videoFileName,
                            isSynced: true
                        });
                        hasChanges = true;
                    }
                }
            }

            if (hasChanges) {
                // Sort by date desc
                localArtworks.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                this.saveMetadata(localArtworks);
                Logger.info('Cloud Sync Complete. Updated local gallery.');
            }

        } catch (e) {
            Logger.error('Error syncing from cloud', e);
        }
    },

    getImageMimeType(fileName: string): string {
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (extension === 'png') return 'image/png';
        if (extension === 'webp') return 'image/webp';
        return 'image/jpeg';
    },

    async createThumbnail(imageBlob: Blob, maxDimension = 384, quality = 0.8): Promise<Blob> {
        const objectUrl = URL.createObjectURL(imageBlob);
        try {
            const image = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = objectUrl;
            });

            const longestSide = Math.max(image.naturalWidth, image.naturalHeight);
            const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
            const width = Math.max(1, Math.round(image.naturalWidth * scale));
            const height = Math.max(1, Math.round(image.naturalHeight * scale));

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                throw new Error('Unable to create thumbnail canvas.');
            }

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(image, 0, 0, width, height);

            const blob = await new Promise<Blob | null>((resolve) => {
                canvas.toBlob(resolve, 'image/jpeg', quality);
            });

            if (!blob) {
                throw new Error('Thumbnail generation failed.');
            }

            return blob;
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    },
};
